import React from "react";
import ChartBuilder from "./ChartBuilder";

export type AgentVisualizationData = {
  title: string;
  columns: { key: string; label: string; type: string; unit?: string | null }[];
  rows: Record<string, unknown>[];
  dimensions: string[];
  measures: string[];
  units?: Record<string, string>;
  date_range?: { start?: string | null; end?: string | null };
  filters?: Record<string, unknown>;
  sources?: { connector: string; entity?: string | null; record_count?: number | null; retrieved_at?: string | null }[];
  completeness_warnings?: string[];
  complete: boolean;
  recommended_chart_type: "bar" | "line" | "pie" | "bubble";
  compatible_chart_types: ("bar" | "line" | "pie" | "bubble")[];
  csv_available: boolean;
};

export const csvCell = (value: unknown): string => {
  const text = value == null ? "" : String(value);
  const formulaSafe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(formulaSafe) ? `"${formulaSafe.replace(/"/g, '""')}"` : formulaSafe;
};

export const visualizationCsv = (data: AgentVisualizationData): string => {
  const keys = data.columns.map((column) => column.key);
  return [
    data.columns.map((column) => csvCell(column.label)).join(","),
    ...data.rows.map((row) => keys.map((key) => csvCell(row[key])).join(",")),
  ].join("\r\n");
};

const numberValue = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? value : Number(value) || 0;

const formatValue = (value: unknown, unit?: string) => {
  if (value == null || value === "") return "—";
  const number = numberValue(value);
  if (unit === "currency") return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(number);
  if (unit === "percentage") return `${number.toLocaleString()}%`;
  return typeof value === "number" ? value.toLocaleString() : String(value ?? "");
};

export default function AgentVisualization({ data }: { data: AgentVisualizationData }) {
  const createChartButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const [showChartBuilder, setShowChartBuilder] = React.useState(false);
  const [showTable, setShowTable] = React.useState(false);
  const closeChartBuilder = React.useCallback(() => {
    setShowChartBuilder(false);
    window.setTimeout(() => createChartButtonRef.current?.focus(), 0);
  }, []);
  const downloadCsv = () => {
    const blob = new Blob(["\uFEFF", visualizationCsv(data)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${data.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "ask-lenses-data"}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const button: React.CSSProperties = { border: "1px solid rgba(96,165,250,.5)", borderRadius: 8, background: "rgba(37,99,235,.16)", color: "#dbeafe", padding: "7px 9px", fontSize: 11, fontWeight: 800, cursor: "pointer" };
  const dateText = data.date_range?.start || data.date_range?.end
    ? `${data.date_range?.start ?? "…"} to ${data.date_range?.end ?? "…"}`
    : "";
  const sourceText = (data.sources ?? []).map((source) => [
    source.connector,
    source.entity,
    source.record_count != null ? `${source.record_count} source records` : "",
  ].filter(Boolean).join(" · ")).join("; ");
  return <section aria-label="Ask Lenses data visualization" style={{ display: "grid", gap: 10, border: "1px solid rgba(148,163,184,.22)", borderRadius: 12, background: "rgba(15,23,42,.72)", padding: 10 }}>
    <div>
      <strong style={{ fontSize: 13 }}>{data.title}</strong>
      <div style={{ color: "#94a3b8", fontSize: 10 }}>{data.rows.length} result rows{dateText ? ` · ${dateText}` : ""}</div>
      {sourceText ? <div style={{ color: "#94a3b8", fontSize: 10 }}>Source: {sourceText}</div> : null}
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <button ref={createChartButtonRef} type="button" style={button} onClick={() => setShowChartBuilder(true)}>Create chart</button>
      {data.csv_available ? <button type="button" style={button} onClick={downloadCsv}>Download CSV</button> : null}
      <button type="button" style={button} onClick={() => setShowTable((value) => !value)}>{showTable ? "Hide source data" : "View source data"}</button>
    </div>
    {data.completeness_warnings?.length ? <div role="alert" style={{ color: "#fde68a", background: "rgba(146,64,14,.25)", padding: 8, borderRadius: 8, fontSize: 11 }}>{data.completeness_warnings.join(" ")}</div> : null}
    {showTable ? <div style={{ overflowX: "auto", maxHeight: 280 }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}><thead><tr>{data.columns.map((column) => <th key={column.key} style={{ textAlign: "left", padding: 5, borderBottom: "1px solid #334155", whiteSpace: "nowrap" }}>{column.label}</th>)}</tr></thead><tbody>{data.rows.map((row, rowIndex) => <tr key={rowIndex}>{data.columns.map((column) => <td key={column.key} style={{ padding: 5, borderBottom: "1px solid rgba(51,65,85,.5)", whiteSpace: "nowrap" }}>{formatValue(row[column.key], column.unit ?? undefined)}</td>)}</tr>)}</tbody></table></div> : null}
    {showChartBuilder ? <ChartBuilder data={data} onClose={closeChartBuilder} /> : null}
  </section>;
}
